import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { SLICE_PAY_HOLD_FEE_RATE, DEFERA_CAPITAL_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { referralSourceLabel } from '@/utils/referralSource';
import { normalizePitchBooking } from '@/utils/pitchBookings';
import { format, parseISO, startOfMonth, subMonths } from 'date-fns';
import {
  TrendingUp, DollarSign, ShoppingCart, BarChart2, Lock, Shield,
  ArrowUpRight, Layers, Zap, ChevronRight, RefreshCw, AlertCircle
} from 'lucide-react';

// ── PIN ──────────────────────────────────────────────────────────────
// Update this PIN by changing both `CORRECT_PIN` here AND the
// `INVESTOR_PITCH_PIN` server env var. The server validates the PIN —
// this client-side constant is only used for the UX gate.
const CORRECT_PIN = '838636';
const PIN_SESSION_KEY = 'defera_pitch_unlocked';
const PIN_VALUE_KEY = 'defera_pitch_pin'; // Stored only in sessionStorage; sent as header
// ─────────────────────────────────────────────────────────────────────

const fmt = (n, digits = 2) =>
  (parseFloat(n) || 0).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtK = (n) => {
  const v = parseFloat(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtInput = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

// ── Helpers ───────────────────────────────────────────────────────────
function getHoldFee(booking) {
  const stored = parseFloat(booking.hold_fee || booking.holdFee || 0);
  if (stored > 0) return stored;
  const pm = booking.flight_data?._payment_method || booking.payment_method || 'installments';
  const isDC = pm === 'defera_capital';
  const isReserve = pm === 'reserve';
  if (isReserve) return 0;
  // grand_total = base_fare + service_fee (already combined).
  // Using it directly avoids double-counting the service fee.
  // Fall back to base + svc only when grand_total isn't stored.
  const grandTotal = parseFloat(booking.grand_total || booking.grandTotal || 0);
  if (grandTotal > 0) {
    return grandTotal * (isDC ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE);
  }
  const base = parseFloat(booking.totalPrice || booking.total_amount || 0);
  const svc  = parseFloat(booking.service_fee || booking.serviceFee || 0);
  return (base + svc) * (isDC ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE);
}

function getGrandTotal(booking) {
  return parseFloat(
    booking.grand_total_with_hold || booking.grandTotalWithHold ||
    booking.grand_total || booking.grandTotal ||
    booking.total_amount || booking.totalPrice || 0
  );
}

// Cancelled/refunded bookings contribute $0 to every traction metric on this
// page (GMV, fees generated, monthly volume, channels, retention, default
// rate). Billed value on a cancelled trip is not revenue — a Defera Zero
// booking cancelled before its first payment billed $0 in real life — and a
// pitch that counts book-and-cancel churn as GMV is the kind of number
// diligence unwinds. Same predicate as Analytics ('canceled' accepted to
// match the server's CANCELLED_STATUSES set).
function isCancelledOrRefunded(booking) {
  const s = booking.status || '';
  return s === 'cancelled' || s === 'canceled' || s === 'refunded';
}

function groupByMonth(bookings) {
  const map = {};
  bookings.forEach((b) => {
    const raw = b.createdAt || b.created_at;
    if (!raw) return;
    const key = format(parseISO(raw), 'MMM yy');
    if (!map[key]) map[key] = { label: key, bookings: 0, gmv: 0, holdFees: 0, order: parseISO(raw).getTime() };
    map[key].bookings += 1;
    map[key].gmv += getGrandTotal(b);
    map[key].holdFees += getHoldFee(b);
  });
  return Object.values(map).sort((a, b) => a.order - b.order);
}

// ── PIN Gate ──────────────────────────────────────────────────────────
function PinGate({ onUnlock }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const inputs = useRef([]);

  const handleDigit = useCallback((index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 filled
    const filled = next.every((d) => d !== '');
    if (filled) {
      const pin = next.join('');
      if (pin === CORRECT_PIN) {
        sessionStorage.setItem(PIN_SESSION_KEY, '1');
        sessionStorage.setItem(PIN_VALUE_KEY, pin);
        onUnlock();
      } else {
        setAttempts((a) => a + 1);
        setShake(true);
        setTimeout(() => {
          setShake(false);
          setDigits(['', '', '', '', '', '']);
          inputs.current[0]?.focus();
        }, 600);
      }
    }
  }, [digits, onUnlock]);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  }, [digits]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(15,40,138,0.25) 0%, #000 60%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="text-center max-w-sm w-full"
      >
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src="/defera-logo-web.png" alt="Defera" className="h-10 w-auto opacity-90" />
        </div>

        <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 font-normal mb-3">Defera Capital</p>
        <h1 className="text-2xl font-light text-white tracking-tight mb-2">Investor Overview</h1>
        <p className="text-white/60 text-sm font-normal mb-10">Enter your 6-digit access code to continue.</p>

        {/* PIN Inputs */}
        <motion.div
          animate={shake ? { x: [-10, 10, -8, 8, -4, 4, 0] } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-3 mb-4"
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              autoFocus={i === 0}
              className={`w-12 h-14 text-center text-xl font-light bg-white/5 border rounded-xl text-white outline-none transition-all
                ${d ? 'border-brand-500/50 bg-brand-500/5' : 'border-white/10'}
                focus:border-brand-500/70 focus:bg-brand-500/8`}
            />
          ))}
        </motion.div>

        {attempts > 0 && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-red-400/80 text-xs font-normal mt-2"
          >
            Incorrect PIN — {attempts} failed {attempts === 1 ? 'attempt' : 'attempts'}
          </motion.p>
        )}

        <p className="text-white/20 text-xs font-normal mt-8">
          This page contains confidential financial information.
        </p>
      </motion.div>
    </div>
  );
}

// ── Metric Card ───────────────────────────────────────────────────────
function MetricCard({ label, value, sub, icon: Icon, accent = 'blue', delay = 0 }) {
  const colors = {
    blue: 'text-brand-400 bg-brand-500/10 border-brand-500/20',
    green: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-brand-400 bg-brand-500/10 border-brand-500/20',
    purple: 'text-brand-400 bg-brand-500/10 border-brand-500/20',
  };
  const iconColor = {
    blue: 'text-brand-400',
    green: 'text-emerald-400',
    amber: 'text-brand-400',
    purple: 'text-brand-400',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className={`p-5 rounded-2xl border bg-white/[0.03] backdrop-blur-sm ${colors[accent]}`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-white/50 text-[11px] uppercase tracking-widest font-normal">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${iconColor[accent]} opacity-70`} />}
      </div>
      <p className="text-white text-2xl font-light tracking-tight">{value}</p>
      {sub && <p className="text-white/40 text-xs font-normal mt-1">{sub}</p>}
    </motion.div>
  );
}

// ── Scenario Row ──────────────────────────────────────────────────────
function ScenarioRow({ label, bookings, avgGMV, holdRate, isHighlight }) {
  const monthlyGMV = bookings * avgGMV;
  const monthlyHold = monthlyGMV * holdRate;
  const annualHold = monthlyHold * 12;

  return (
    <div className={`grid grid-cols-4 gap-4 px-4 py-4 rounded-xl ${isHighlight ? 'bg-brand-500/8 border border-brand-500/20' : 'bg-white/[0.02] border border-white/5'}`}>
      <div>
        <p className={`text-sm font-normal ${isHighlight ? 'text-brand-300' : 'text-white/85'}`}>{label}</p>
        <p className="text-white/40 text-xs">{bookings.toLocaleString()} bookings/mo</p>
      </div>
      <div className="text-right">
        <p className="text-white/70 text-sm font-normal">{fmtK(monthlyGMV)}</p>
        <p className="text-white/30 text-xs">monthly GMV</p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-normal ${isHighlight ? 'text-emerald-400' : 'text-emerald-400/80'}`}>{fmtK(monthlyHold)}</p>
        <p className="text-white/30 text-xs">hold rev/mo</p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-normal ${isHighlight ? 'text-emerald-400' : 'text-emerald-400/70'}`}>{fmtK(annualHold)}</p>
        <p className="text-white/30 text-xs">annual rev</p>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────
function InvestorDashboard() {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scenario builder state
  const [scenarioBookings, setScenarioBookings] = useState(200);
  const [scenarioInitialized, setScenarioInitialized] = useState(false);
  const [capitalAmount, setCapitalAmount] = useState(100000);
  const [marketingBudget, setMarketingBudget] = useState(2000);
  // null = use calculated value from data; number = manual override
  const [cpbOverride, setCpbOverride] = useState(null);

  useEffect(() => {
    // The pitch-data endpoint is gated server-side: PIN header or admin/investor Bearer.
    const loadData = async () => {
      try {
        const headers = {};
        const pin = sessionStorage.getItem(PIN_VALUE_KEY);
        if (pin) headers['X-Pitch-Pin'] = pin;

        // Also pass an admin/investor Bearer token if available (e.g. staff viewing the pitch).
        try {
          const { supabase } = await import('@/lib/supabase');
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        } catch (_) { /* non-fatal */ }

        const res = await fetch('/api/investor/pitch-data', { headers });
        if (res.status === 401) {
          // PIN/auth invalid (e.g. PIN rotated). Force re-entry.
          sessionStorage.removeItem(PIN_SESSION_KEY);
          sessionStorage.removeItem(PIN_VALUE_KEY);
          throw new Error('Pitch access expired. Please re-enter your PIN.');
        }
        if (!res.ok) throw new Error(`pitch-data returned ${res.status}`);
        const { bookings: rawBookings, users: rawUsers } = await res.json();

        // Normalise snake_case from DB into the camelCase shape the rest of the
        // component already expects (same mapping FlightBooking constructor does),
        // with any Faye premium taken out: every figure here is traction.
        const bd = (rawBookings || []).map(normalizePitchBooking);

        setBookings(bd);
        setUsers(rawUsers || []);
        // Default scenario to current pace + 30% once we have real data.
        // Pace is net of cancellations — seeding a growth scenario from
        // book-and-cancel churn would bake the inflation into every slider.
        if (!scenarioInitialized && bd.length > 0) {
          const monthly = groupByMonth(bd.filter((b) => !isCancelledOrRefunded(b)));
          const last12m = monthly.slice(-12);
          const pace = last12m.length > 0
            ? last12m.reduce((s, m) => s + m.bookings, 0) / last12m.length
            : bd.length / 12 || 1;
          setScenarioBookings(Math.max(10, Math.round(pace * 1.3)));
          setScenarioInitialized(true);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load pitch data:', err);
        setError('Unable to load live data.');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // ── Derived metrics ────────────────────────────────────────────────
  const allBookings = bookings;

  // The traction cohort: every $ metric, chart, and rate below runs on this.
  // allBookings survives only for created-count context (the Total Bookings
  // tile) and pricing-shape stats (fee tiers, schedule windows), where a
  // cancelled booking's billed structure is still a valid datapoint.
  const activeBookings = allBookings.filter((b) => !isCancelledOrRefunded(b));
  const cancelledBookingsCount = allBookings.length - activeBookings.length;

  const totalBookings = allBookings.length;
  const confirmedBookings = allBookings.filter(
    (b) => b.status === 'confirmed' || b.status === 'completed' || b.status === 'paid'
  ).length;

  // ── Default / cancellation rate — installment bookings only ────────
  // Only installment-type bookings carry default risk (reserve has no capital
  // deployed). Net of cancellations: a cancelled booking can never default,
  // so leaving churn in the denominator understates the default rate.
  const installmentBookings = activeBookings.filter((b) => {
    const pm = b.flight_data?._payment_method || b.payment_method || 'installments';
    return pm === 'installments' || pm === 'defera_capital';
  });
  const defaultedInstallments = installmentBookings.filter((b) =>
    b.status === 'defaulted'
  );
  const defaultRate = installmentBookings.length > 0
    ? defaultedInstallments.length / installmentBookings.length
    : 0;
  const defaultCount = defaultedInstallments.length;

  // ── Default rate by deposit percentage tier ─────────────────────────
  // Group installment bookings into deposit tiers and calculate default rate per tier.
  // Deposit % comes from the stored payment_schedule; fall back to 20% if missing.
  // LIVE_DEPOSIT_TIERS = currently offered options (Slice Pay: 20/30/40/50,
  // Defera Capital effective 2026-06-16: 5/10/20/30/40/50 at 90+ day lead).
  const LIVE_DEPOSIT_TIERS = new Set([5, 10, 20, 30, 40, 50]);
  const depositTierMap = {};
  installmentBookings.forEach((b) => {
    const ps = b.flight_data?.payment_schedule || b.flightData?.payment_schedule;
    const rawPct = parseFloat(ps?.depositPercentage ?? ps?.deposit_percentage ?? 20);
    // Round to nearest 5% tier for clean grouping
    const tier = Math.round(rawPct / 5) * 5;
    if (!depositTierMap[tier]) depositTierMap[tier] = { total: 0, defaulted: 0 };
    depositTierMap[tier].total += 1;
    if (b.status === 'defaulted') depositTierMap[tier].defaulted += 1;
  });
  const depositTierRows = Object.entries(depositTierMap)
    .map(([pct, { total, defaulted }]) => ({
      pct: parseInt(pct),
      total,
      defaulted,
      rate: total > 0 ? defaulted / total : 0,
    }))
    .sort((a, b) => a.pct - b.pct);

  const totalGMV = activeBookings.reduce((s, b) => s + getGrandTotal(b), 0);
  const totalHoldFees = activeBookings.reduce((s, b) => s + getHoldFee(b), 0);
  const totalServiceFees = activeBookings.reduce((s, b) => s + parseFloat(b.service_fee || b.serviceFee || 0), 0);
  const avgGMV = activeBookings.length > 0 ? totalGMV / activeBookings.length : 0;
  const avgHoldFee = activeBookings.length > 0 ? totalHoldFees / activeBookings.length : 0;
  const avgHoldRate = totalGMV > 0 ? totalHoldFees / (totalGMV - totalHoldFees) : SLICE_PAY_HOLD_FEE_RATE;
  // Base fare = ticket cost fronted per booking (must be declared before recentAvgBaseFare)
  const avgBaseFare = activeBookings.length > 0
    ? activeBookings.reduce((s, b) => s + parseFloat(b.totalPrice || b.total_amount || 0), 0) / activeBookings.length
    : 750;

  // Monthly trend (last 12 months, net of cancellations)
  const monthlyData = groupByMonth(activeBookings);
  const last12 = monthlyData.slice(-12);

  // Current month and prior month
  const currentMonth = last12[last12.length - 1];
  const priorMonth = last12[last12.length - 2];

  // Extrapolate current (partial) month to a full-month run rate.
  // e.g. 10 bookings on day 20 of a 30-day month → projected 15/mo.
  const today = new Date();
  const dayOfMonth = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const currentMonthProjected = currentMonth && dayOfMonth > 0
    ? Math.round(currentMonth.bookings * (daysInMonth / dayOfMonth))
    : priorMonth?.bookings || 0;

  // MoM growth using projected full-month rate vs last completed month
  const momGrowth = currentMonthProjected && priorMonth && priorMonth.bookings > 0
    ? ((currentMonthProjected - priorMonth.bookings) / priorMonth.bookings) * 100
    : null;

  // Recent bookings — Dec 2025+ to reflect current pricing and volume, not historical lows.
  const recentCutoff = new Date('2025-12-01');
  const recentBookings = activeBookings.filter((b) => {
    const d = b.createdAt || b.created_at;
    return d && new Date(d) >= recentCutoff;
  });
  const recentTotalGMV = recentBookings.reduce((s, b) => s + getGrandTotal(b), 0);
  const recentTotalHoldFees = recentBookings.reduce((s, b) => s + getHoldFee(b), 0);
  const recentTotalBaseFares = recentBookings.reduce((s, b) => s + parseFloat(b.totalPrice || b.total_amount || 0), 0);
  const recentAvgGMV = recentBookings.length > 0 ? recentTotalGMV / recentBookings.length : avgGMV;
  const recentAvgHoldFee = recentBookings.length > 0 ? recentTotalHoldFees / recentBookings.length : avgHoldFee;
  const recentAvgBaseFare = recentBookings.length > 0 ? recentTotalBaseFares / recentBookings.length : avgBaseFare;

  // Payment method breakdown — merge installments + defera_capital under one label
  const methodCounts = {};
  activeBookings.forEach((b) => {
    const raw = b.flight_data?._payment_method || b.payment_method || 'installments';
    const pm = (raw === 'defera_capital' || raw === 'installments') ? 'installments' : raw;
    methodCounts[pm] = (methodCounts[pm] || 0) + 1;
  });

  // ── Referral source map: userId → referral_source from user table ────
  const userReferralMap = {};
  users.forEach((u) => {
    if (u.id && u.referral_source) {
      userReferralMap[u.id] = u.referral_source.toLowerCase().trim();
    }
  });

  // Normalize common referral source labels
  const normalizeSource = (raw) => {
    if (!raw) return 'direct';
    const s = raw.toLowerCase().trim();
    if (s.includes('reddit')) return 'reddit';
    if (s.includes('friend') || s.includes('referral')) return 'friend referral';
    if (s.includes('tiktok') || s.includes('tik tok')) return 'tiktok';
    if (s.includes('google')) return 'google';
    if (s.includes('instagram') || s.includes('ig')) return 'instagram';
    if (s.includes('twitter') || s.includes('x.com')) return 'twitter/x';
    return s || 'direct';
  };

  // ── Customer retention / repeat rate from real booking data ─────────
  // Only use bookings since Nov 2025 (when the platform had meaningful volume).
  // Net of cancellations: a customer who books the same route seven times and
  // cancels six is one net booking, not a "repeat customer" — counting churn
  // here inflated repeat rate, LTV, and the recurring-revenue channel at once.
  const DATA_START = new Date('2025-11-01');
  const relevantBookings = activeBookings.filter((b) => {
    const d = b.createdAt || b.created_at;
    return d && new Date(d) >= DATA_START;
  });

  const customerBookingMap = {};
  relevantBookings.forEach((b) => {
    const uid = b.userId || b.user_id;
    if (!uid) return;
    customerBookingMap[uid] = (customerBookingMap[uid] || 0) + 1;
  });
  const uniqueCustomers = Object.keys(customerBookingMap).length;
  const repeatCustomers = Object.values(customerBookingMap).filter(n => n > 1).length;
  const avgBookingsPerCustomer = uniqueCustomers > 0
    ? relevantBookings.length / uniqueCustomers
    : 1;
  const repeatRate = uniqueCustomers > 0 ? repeatCustomers / uniqueCustomers : 0;
  const customerLTV = avgBookingsPerCustomer * recentAvgHoldFee;

  // Monthly repeat booking rate: how many additional bookings does each existing customer
  // generate per month on average, derived from actual platform history.
  const earliestRelevant = relevantBookings.reduce((min, b) => {
    const d = new Date(b.createdAt || b.created_at);
    return d < min ? d : min;
  }, new Date());
  const platformMonths = Math.max(1,
    (new Date() - earliestRelevant) / (1000 * 60 * 60 * 24 * 30)
  );
  // Total repeat bookings = total - unique (each customer's first booking = 1, rest are repeats)
  const totalRepeatBookings = Math.max(0, relevantBookings.length - uniqueCustomers);
  // Per-customer monthly repeat rate
  const monthlyRepeatRate = uniqueCustomers > 0
    ? totalRepeatBookings / (uniqueCustomers * platformMonths)
    : 0;

  // ── Organic traffic breakdown for relevant (Nov 2025+) bookings ─────
  // Categorize every booking by channel.
  // A user's FIRST booking is attributed to their referral source (how we acquired them).
  // Their 2nd+ bookings are "recurring" — free repeat revenue at $0 acquisition cost.
  const organicChannelCounts = {};
  const organicChannelGMV = {};
  let recurringBookingCount = 0;
  let firstTimeBookingCount = 0;

  // Sort relevant bookings by date so we can track which is each user's first
  const sortedRelevant = [...relevantBookings].sort((a, b) => {
    const da = new Date(a.createdAt || a.created_at);
    const db = new Date(b.createdAt || b.created_at);
    return da - db;
  });
  const userFirstBookingSeen = {};

  sortedRelevant.forEach((b) => {
    const uid = b.userId || b.user_id;
    const totalForUser = uid ? customerBookingMap[uid] || 1 : 1;
    const hasSeenBefore = uid ? !!userFirstBookingSeen[uid] : false;
    // Mark this as "recurring" only if this is NOT the user's first booking in our dataset
    const isRecurring = totalForUser > 1 && hasSeenBefore;
    if (uid) userFirstBookingSeen[uid] = true;

    const rawSource = uid ? userReferralMap[uid] : null;
    const channel = isRecurring ? 'recurring customer' : normalizeSource(rawSource);

    organicChannelCounts[channel] = (organicChannelCounts[channel] || 0) + 1;
    organicChannelGMV[channel] = (organicChannelGMV[channel] || 0) + getGrandTotal(b);

    if (isRecurring) recurringBookingCount++;
    else firstTimeBookingCount++;
  });

  // Sort channels by booking count descending
  const organicChannelRows = Object.entries(organicChannelCounts)
    .map(([channel, count]) => ({
      channel,
      count,
      gmv: organicChannelGMV[channel] || 0,
      pct: relevantBookings.length > 0 ? count / relevantBookings.length : 0,
    }))
    .sort((a, b) => b.count - a.count);

  const recurringPct = relevantBookings.length > 0
    ? recurringBookingCount / relevantBookings.length
    : 0;

  // ── Capital / Carry calculations ───────────────────────────────────

  // ── Payment schedule analysis — real data only ─────────────────────
  // Only use bookings with an actual stored payment_schedule in flight_data.
  // No departure-date fallback — if there's no schedule, the booking is excluded.
  const scheduleData = allBookings
    .map((b) => {
      const created = b.createdAt || b.created_at;
      if (!created) return null;

      const ps = b.flight_data?.payment_schedule || b.flightData?.payment_schedule;
      if (!ps) return null;

      // Prefer weekly final date, fall back to bi-weekly
      const finalPaymentStr =
        ps.weeklyPlan?.finalPaymentDate ||
        ps.biweeklyPlan?.finalPaymentDate ||
        ps.final_payment_date;
      if (!finalPaymentStr) return null;

      const days = Math.floor((new Date(finalPaymentStr) - new Date(created)) / 86_400_000);
      if (days <= 0 || days >= 365) return null;

      return {
        days,
        depositPct:      parseFloat(ps.depositPercentage  ?? ps.deposit_percentage  ?? 20),
        depositAmount:   parseFloat(ps.depositAmount       ?? ps.deposit_amount      ?? 0),
        remainingBal:    parseFloat(ps.remainingBalance    ?? ps.remaining_balance   ?? 0),
        holdFeeAmount:   parseFloat(ps.holdFeeAmount       ?? ps.hold_fee_amount     ?? 0),
        totalAfterHold:  parseFloat(ps.totalAfterHoldFee   ?? ps.total_after_hold_fee ?? 0),
        weeklyPayments:  ps.weeklyPlan?.paymentCount       ?? 0,
        biweeklyPayments:ps.biweeklyPlan?.paymentCount     ?? 0,
      };
    })
    .filter(Boolean);

  const hasScheduleData = scheduleData.length > 0;
  const scheduleCount = scheduleData.length;

  const avgWindowDays = hasScheduleData
    ? Math.round(scheduleData.reduce((s, d) => s + d.days, 0) / scheduleCount)
    : 52;

  const avgDepositPct = hasScheduleData
    ? scheduleData.reduce((s, d) => s + d.depositPct, 0) / scheduleCount
    : 20;

  // Effective revolving capital is reduced by deposits received upfront.
  // After deposit, only the remaining balance portion is still at risk.
  // Use recentAvgBaseFare (Dec 2025+) to reflect current booking sizes accurately.
  const capitalAtRiskFactor = 1 - (avgDepositPct / 100);
  const avgNetCapitalAtRisk = recentAvgBaseFare * capitalAtRiskFactor;

  // Monthly booking pace from last 12 months.
  // Replace the partial current month with the extrapolated full-month rate
  // so revolving capital, annualized yield, and monthly hold income aren't understated.
  const last12WithProjected = last12.map((m, i) =>
    i === last12.length - 1 ? { ...m, bookings: currentMonthProjected } : m
  );
  const monthlyPace = last12WithProjected.length > 0
    ? last12WithProjected.reduce((s, m) => s + m.bookings, 0) / last12WithProjected.length
    : totalBookings / 12 || 1;

  // Revolving capital at steady state — use currentMonthProjected so this stays
  // consistent with the "Current Pace" row in the scenario table.
  const revolvingCapital = currentMonthProjected * avgNetCapitalAtRisk * (avgWindowDays / 30);

  // Monthly hold fee income at current pace — use recent avg hold fee (Dec 2025+)
  const monthlyHoldAtPace = currentMonthProjected * recentAvgHoldFee;

  // Annualised yield on revolving capital
  const annualYield = revolvingCapital > 0
    ? (monthlyHoldAtPace * 12) / revolvingCapital
    : 0;

  // Per-booking yield (hold fee / base fare) — use recent figures
  const perBookingYield = recentAvgBaseFare > 0 ? recentAvgHoldFee / recentAvgBaseFare : 0;

  // ── Marketing channel calculations ─────────────────────────────────
  // Ads ran at ~$2K/mo until December 2025. TikTok influencers ~$200/mo ongoing.
  // Split monthly data into ad-period vs organic-period to derive cost-per-booking.
  const AD_STOP_KEY = '2025-12'; // month ads stopped (Dec 2025)
  const PEAK_AD_SPEND = 2000;    // monthly ad spend during peak period
  // NOTE: TIKTOK_EFFICIENCY is an assumed multiplier (TikTok influencer spend is
  // typically more targeted than broad paid ads). Not derived from platform data.
  const TIKTOK_EFFICIENCY = 1.4;

  const parseMonthKey = (label) => {
    const [mon, yr] = label.split(' ');
    const monthMap = { Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12 };
    const yrNum = parseInt('20' + yr);
    const monNum = monthMap[mon] || 1;
    return `${yrNum}-${String(monNum).padStart(2,'0')}`;
  };

  const adPeriodMonths = monthlyData.filter((m) => {
    const key = parseMonthKey(m.label);
    return key >= '2025-06' && key < AD_STOP_KEY;
  });

  const lowSpendMonths = monthlyData.filter((m) => {
    const key = parseMonthKey(m.label);
    return key >= AD_STOP_KEY;
  });

  const adPeriodAvg = adPeriodMonths.length > 0
    ? adPeriodMonths.reduce((s, m) => s + m.bookings, 0) / adPeriodMonths.length
    : null; // null = no data yet for this period

  // Replace the last (partial) month's bookings with the projected full-month rate
  // so the organic baseline isn't understated when the current month is incomplete.
  const lowSpendAvg = lowSpendMonths.length > 0
    ? lowSpendMonths.reduce((s, m, i) =>
        s + (i === lowSpendMonths.length - 1 ? currentMonthProjected : m.bookings), 0
      ) / lowSpendMonths.length
    : monthlyPace;

  // Use current pace as the low-spend reference regardless
  const organicAvg = lowSpendAvg;

  // Only calculate lift if we have real ad-period data AND a meaningful gap (≥1 booking difference).
  // A smaller gap means the two periods are too similar to derive a reliable cost-per-booking.
  const hasAdPeriodData = adPeriodAvg !== null;
  const rawIncremental = hasAdPeriodData ? adPeriodAvg - organicAvg : null;
  const liftIsMeaningful = rawIncremental !== null && rawIncremental >= 1;
  const adIncrementalBookings = liftIsMeaningful ? rawIncremental : null;

  // Cost per incremental booking — only shown when lift is meaningful
  const costPerBookingAds = liftIsMeaningful
    ? PEAK_AD_SPEND / adIncrementalBookings
    : null;
  const costPerBookingTikTok = costPerBookingAds !== null
    ? costPerBookingAds / TIKTOK_EFFICIENCY
    : null;

  // Scenario builder — use recent (Dec 2025+) averages so projections reflect
  // current booking sizes and hold fees, not historical lows.
  const scenarioAvgGMV = recentAvgGMV > 0 ? recentAvgGMV : avgGMV > 0 ? avgGMV : 750;
  const scenarioHoldRate = avgHoldRate > 0 ? avgHoldRate : SLICE_PAY_HOLD_FEE_RATE;
  const scenarioAvgHoldFeePerBooking = recentAvgHoldFee > 0 ? recentAvgHoldFee : scenarioAvgGMV * SLICE_PAY_HOLD_FEE_RATE;
  const scenarioMonthlyHold = scenarioBookings * scenarioAvgHoldFeePerBooking;
  const scenarioAnnualHold = scenarioMonthlyHold * 12;

  // Fee tier breakdown (from real data)
  const tier1 = allBookings.filter((b) => {
    const base = parseFloat(b.totalPrice || b.total_amount || 0);
    return base < 200;
  });
  const tier2 = allBookings.filter((b) => {
    const base = parseFloat(b.totalPrice || b.total_amount || 0);
    return base >= 200 && base < 700;
  });
  const tier3 = allBookings.filter((b) => {
    const base = parseFloat(b.totalPrice || b.total_amount || 0);
    return base >= 700;
  });

  const avgHoldTier = (list) => {
    if (!list.length) return 0;
    return list.reduce((s, b) => s + getHoldFee(b), 0) / list.length;
  };

  const customTooltipStyle = {
    backgroundColor: 'rgba(0,0,0,0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    color: '#fff',
    fontWeight: 500,
    fontSize: '12px',
    padding: '8px 12px',
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(15,35,103,0.18) 0%, #000 55%)', zoom: 1.12 }}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">

        {/* ── Header ── */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20">
              <Shield className="w-3 h-3 text-brand-400" />
              <span className="text-brand-400 text-[10px] uppercase tracking-widest font-normal">Confidential</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/50 text-[10px] uppercase tracking-widest font-normal">Live Data</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-extralight text-white tracking-tight mb-3">
            Defera <span className="text-white/30">/</span> Investor Overview
          </h1>
          <p className="text-white/60 text-base font-normal max-w-xl leading-relaxed">
            Platform economics and revenue opportunity for an installment capital partner. All figures
            derived from live booking data.
          </p>
        </motion.div>

        {/* ── Loading / Error ── */}
        {loading && (
          <div className="flex items-center gap-2 text-white/40 mb-12">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm font-normal">Loading platform data…</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-red-400/80 mb-12 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-normal">{error}</span>
          </div>
        )}

        {/* ── Top Metrics ── */}
        {!loading && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              <MetricCard label="Total Bookings" value={totalBookings.toLocaleString()} sub={`${confirmedBookings} confirmed · ${cancelledBookingsCount} cancelled`} icon={BarChart2} accent="blue" delay={0.05} />
              <MetricCard label="Total GMV" value={fmtK(totalGMV)} sub="booked volume · net of cancellations" icon={DollarSign} accent="green" delay={0.1} />
              <MetricCard label="Hold Fees Generated" value={fmtK(totalHoldFees)} sub={`avg ${fmt(avgHoldRate * 100, 1)}% of subtotal`} icon={TrendingUp} accent="amber" delay={0.15} />
              <MetricCard label="Avg Booking Size" value={`$${fmt(recentAvgGMV, 0)}`} sub={`avg hold fee $${fmt(recentAvgHoldFee, 0)}/booking (Dec 2025+)`} icon={Layers} accent="purple" delay={0.2} />
            </div>

            {/* ── Revenue Opportunity Callout ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-10 p-6 md:p-8 rounded-2xl border border-brand-500/25 bg-gradient-to-br from-brand-950/30 to-black/60 backdrop-blur-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-brand-400/70 font-normal mb-2">The Opportunity</p>
                  <h2 className="text-2xl font-light text-white tracking-tight mb-2">Take over the Hold Fee stream</h2>
                  <p className="text-white/65 text-sm font-normal leading-relaxed max-w-lg">
                    Defera currently routes installment capital through a third-party provider.
                    An investor stepping in as the capital source earns <span className="text-white/80">100% of the Hold Fee</span> on every qualifying booking.
                    No app changes. No customer disruption. Pure spread income.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-white/30 text-xs font-normal mb-1">Current hold fee rate</p>
                  <p className="text-4xl font-light text-emerald-400 tracking-tight">15%</p>
                  <p className="text-white/40 text-xs">of base fare + service fee</p>
                </div>
              </div>
            </motion.div>

            {/* ── Monthly Trends ── */}
            {last12.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
              >
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-light text-white tracking-tight">Monthly Performance</h2>
                  {momGrowth !== null && (
                    <div className={`flex items-center gap-1 text-sm font-normal ${momGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      <ArrowUpRight className={`w-4 h-4 ${momGrowth < 0 ? 'rotate-180' : ''}`} />
                      {Math.abs(momGrowth).toFixed(1)}% MoM
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* GMV Chart — counter-zoom so Recharts mouse coords stay 1:1 */}
                  <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <p className="text-white/40 text-[11px] uppercase tracking-widest mb-4">Gross Booking Volume</p>
                    <div className="h-40" style={{ zoom: 1 / 1.12 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={last12} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#2A3868" stopOpacity={0.3} />
                              <stop offset="100%" stopColor="#2A3868" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="label" tick={{ fill: '#ffffff30', fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip
                            contentStyle={customTooltipStyle}
                            formatter={(v) => [`$${fmt(v, 0)}`, 'GMV']}
                            wrapperStyle={{ zIndex: 50 }}
                          />
                          <Area type="monotone" dataKey="gmv" stroke="#2A3868" strokeWidth={2} fill="url(#gmvGrad)" isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Hold Fee Chart — counter-zoom so Recharts mouse coords stay 1:1 */}
                  <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <p className="text-white/40 text-[11px] uppercase tracking-widest mb-4">Hold Fee Revenue</p>
                    <div className="h-40" style={{ zoom: 1 / 1.12 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={last12} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                          <XAxis dataKey="label" tick={{ fill: '#ffffff30', fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip
                            contentStyle={customTooltipStyle}
                            formatter={(v) => [`$${fmt(v, 0)}`, 'Hold Fees']}
                            wrapperStyle={{ zIndex: 50 }}
                          />
                          <Bar dataKey="holdFees" fill="#10b981" radius={[3, 3, 0, 0]} opacity={0.75} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Fee Tier Breakdown ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-10"
            >
              <h2 className="text-lg font-light text-white tracking-tight mb-5">Fee Structure — Real Booking Data</h2>

              <div className="overflow-x-auto rounded-2xl border border-white/8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8">
                      <th className="text-left text-white/40 text-[11px] uppercase tracking-widest font-normal px-5 py-3">Fare Tier</th>
                      <th className="text-right text-white/40 text-[11px] uppercase tracking-widest font-normal px-5 py-3">Service Fee</th>
                      <th className="text-right text-white/40 text-[11px] uppercase tracking-widest font-normal px-5 py-3">Hold Fee Rate</th>
                      <th className="text-right text-white/40 text-[11px] uppercase tracking-widest font-normal px-5 py-3">Actual Bookings</th>
                      <th className="text-right text-white/40 text-[11px] uppercase tracking-widest font-normal px-5 py-3">Avg Hold Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Under $200', svc: '40% (30% DC)', tier: tier1 },
                      { label: '$200 – $699', svc: '10%', tier: tier2 },
                      { label: '$700+', svc: '8%', tier: tier3 },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-white/5 ${i === 2 ? 'border-none' : ''}`}>
                        <td className="text-white/85 font-normal px-5 py-3.5">{row.label}</td>
                        <td className="text-white/50 font-normal text-right px-5 py-3.5">{row.svc}</td>
                        <td className="text-brand-400 font-normal text-right px-5 py-3.5">15%</td>
                        <td className="text-white/60 font-normal text-right px-5 py-3.5">{row.tier.length.toLocaleString()}</td>
                        <td className="text-emerald-400 font-medium text-right px-5 py-3.5">${fmt(avgHoldTier(row.tier), 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-white/25 text-xs font-normal mt-2 px-1">
                Hold fee applied to (base fare + service fee). Excludes Reserve bookings (no hold fee) and flight+hotel packages (service fee waived).
              </p>
            </motion.div>

            {/* ── Capital Compounding ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h2 className="text-lg font-light text-white tracking-tight">How Capital Recycles & Compounds</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-7">
                Capital isn't locked up for a year — it comes back in installments throughout each booking's repayment window, then immediately goes back to work on the next flight. And if hold fee earnings are reinvested rather than withdrawn, the pool grows over time — backing more volume without adding new money.
              </p>

              {/* Recycling explainer — simple flow */}
              <div className="mb-6 p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/30 text-[10px] uppercase tracking-widest font-normal mb-4">The Cycle — Per Booking</p>
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-0">
                  {[
                    { step: 'Deploy', desc: `$${fmt(recentAvgBaseFare, 0)} fronted`, color: 'bg-brand-500/20 border-brand-500/30 text-brand-300' },
                    { step: 'Installments In', desc: `weekly/bi-weekly over ~${avgWindowDays}d`, color: 'bg-white/5 border-white/10 text-white/60' },
                    { step: 'Capital Returned', desc: 'principal recovered', color: 'bg-white/5 border-white/10 text-white/60' },
                    { step: '+ Hold Fee', desc: `$${fmt(recentAvgHoldFee, 0)} profit kept`, color: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' },
                    { step: 'Redeploy', desc: 'same capital, new booking', color: 'bg-brand-500/20 border-brand-500/30 text-brand-300' },
                  ].map((item, i, arr) => (
                    <React.Fragment key={item.step}>
                      <div className={`flex-1 px-3 py-2.5 rounded-xl border text-center ${item.color}`}>
                        <p className="text-[10px] uppercase tracking-widest font-medium mb-0.5">{item.step}</p>
                        <p className="text-[11px] font-normal opacity-80">{item.desc}</p>
                      </div>
                      {i < arr.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-white/20 flex-shrink-0 rotate-90 md:rotate-0 mx-1" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-white/20 text-[11px] font-normal mt-3">
                  Capital doesn't sit idle between cycles. As each installment arrives, that portion is freed and available for the next booking immediately.
                </p>
              </div>

              {/* Recycling stats */}
              {(() => {
                const turnsPerYear = avgWindowDays > 0 ? parseFloat((365 / avgWindowDays).toFixed(1)) : 6;
                const monthsToDouble = annualYield > 0
                  ? Math.ceil(Math.log(2) / Math.log(1 + annualYield / 12))
                  : null;

                return (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 rounded-2xl border border-brand-500/20 bg-brand-500/5 text-center">
                      <p className="text-white text-2xl font-light">{turnsPerYear}×</p>
                      <p className="text-white/40 text-xs font-normal mt-1">capital turns per year</p>
                      <p className="text-white/25 text-[10px] mt-0.5">365 ÷ {avgWindowDays}-day avg hold</p>
                    </div>
                    <div className="p-4 rounded-2xl border border-brand-500/20 bg-brand-500/5 text-center">
                      <p className="text-brand-400 text-2xl font-light">{fmt(annualYield * 100, 1)}%</p>
                      <p className="text-white/40 text-xs font-normal mt-1">annualised yield on capital</p>
                      <p className="text-white/25 text-[10px] mt-0.5">assumes full deployment — actual yield scales with utilisation</p>
                    </div>
                    <div className="p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 text-center">
                      {monthsToDouble !== null ? (
                        <>
                          <p className="text-emerald-400 text-2xl font-light">{monthsToDouble} mo</p>
                          <p className="text-white/40 text-xs font-normal mt-1">to double capacity</p>
                          <p className="text-white/25 text-[10px] mt-0.5">if hold fees are reinvested</p>
                        </>
                      ) : (
                        <>
                          <p className="text-white/30 text-2xl font-light">—</p>
                          <p className="text-white/40 text-xs font-normal mt-1">to double capacity</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Compounding projection table */}
              <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/30 text-[10px] uppercase tracking-widest font-normal mb-1">Capital Growth Projection</p>
                <p className="text-white/25 text-xs font-normal mb-5">
                  Starting from ${fmtInput(capitalAmount)} — what happens if hold fees are fully reinvested each year. No new money added. Payback window derived from {hasScheduleData ? `${scheduleCount} actual stored payment schedules` : 'estimated averages (no schedule data found)'}. Avg deposit collected upfront: {fmt(avgDepositPct, 0)}% — reducing net capital at risk per booking to ~${fmt(avgNetCapitalAtRisk, 0)}.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/8">
                        {['Year', 'Capital Pool', 'Monthly Volume Backed', 'Monthly Hold Fees', 'Cumulative Earned'].map(h => (
                          <th key={h} className="text-left text-white/30 text-[10px] uppercase tracking-widest font-normal px-3 py-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[0, 1, 2, 3, 5, 10].map((year) => {
                        const capital = capitalAmount * Math.pow(1 + annualYield, year);
                        const bookingsPerMonth = avgNetCapitalAtRisk > 0 && avgWindowDays > 0
                          ? capital / (avgNetCapitalAtRisk * (avgWindowDays / 30))
                          : 0;
                        const monthlyVolume = bookingsPerMonth * recentAvgGMV;
                        const monthlyHold = bookingsPerMonth * recentAvgHoldFee;
                        const cumEarned = capital - capitalAmount;
                        const isNow = year === 0;
                        return (
                          <tr key={year} className={`border-b border-white/5 ${isNow ? '' : ''}`}>
                            <td className="px-3 py-3 text-white/60 font-normal">
                              {isNow ? <span className="text-brand-400">Now</span> : `Year ${year}`}
                            </td>
                            <td className="px-3 py-3">
                              <span className={`font-normal ${isNow ? 'text-white/70' : 'text-white'}`}>
                                ${Math.round(capital).toLocaleString()}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-white/60 font-normal">${Math.round(monthlyVolume).toLocaleString()}/mo</td>
                            <td className="px-3 py-3 text-emerald-400 font-normal">
                              ${Math.round(monthlyHold).toLocaleString()}/mo
                            </td>
                            <td className="px-3 py-3 font-normal">
                              {isNow
                                ? <span className="text-white/25">—</span>
                                : <span className="text-emerald-400">+${Math.round(cumEarned).toLocaleString()}</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-white/20 text-[11px] font-normal mt-3 leading-relaxed">
                  Reinvesting hold fees means the capital pool grows, which supports more monthly volume, which earns more hold fees — a self-reinforcing cycle. Withdrawing fees instead keeps capacity flat but provides steady cash income.
                </p>
              </div>
            </motion.div>

            {/* ── Scenario Builder ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-10 p-6 md:p-8 rounded-2xl border border-white/8 bg-white/[0.02]"
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-brand-400" />
                <h2 className="text-lg font-light text-white tracking-tight">Revenue Scenario Builder</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-7">
                Adjust monthly booking volume to model investor revenue. Average booking size and hold rate are calculated from live data.
              </p>

              {/* Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <div>
                  <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Monthly Bookings</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fmtInput(scenarioBookings)}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value.replace(/,/g, '')) || 1;
                        setScenarioBookings(Math.max(1, raw));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                    />
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={10000}
                    step={10}
                    value={scenarioBookings}
                    onChange={(e) => setScenarioBookings(parseInt(e.target.value))}
                    className="w-full mt-3 accent-brand-500"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Avg Booking Size (GMV)</label>
                  <div className="bg-white/5 border border-white/8 rounded-xl px-4 py-3 flex items-center justify-between">
                    <span className="text-white text-base font-normal">${fmt(scenarioAvgGMV, 0)}</span>
                    <span className="text-white/30 text-xs">from live data</span>
                  </div>
                  <p className="text-white/25 text-xs mt-2 font-normal">
                    {recentBookings.length > 0
                      ? `Dec 2025+ avg — ${recentBookings.length} recent bookings`
                      : `All-time avg — ${totalBookings} bookings`}
                  </p>
                </div>
                <div>
                  <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Hold Fee Rate</label>
                  <div className="bg-white/5 border border-white/8 rounded-xl px-4 py-3 flex items-center justify-between">
                    <span className="text-brand-400 text-base font-normal">{fmt(scenarioHoldRate * 100, 1)}%</span>
                    <span className="text-white/30 text-xs">actual avg rate</span>
                  </div>
                </div>
              </div>

              {/* Output cards */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="p-4 rounded-xl bg-black/40 border border-white/8 text-center">
                  <p className="text-white/40 text-[11px] uppercase tracking-widest mb-2">Monthly GMV</p>
                  <p className="text-white text-xl font-light">{fmtK(scenarioBookings * scenarioAvgGMV)}</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-center">
                  <p className="text-emerald-400/70 text-[11px] uppercase tracking-widest mb-2">Monthly Revenue</p>
                  <p className="text-emerald-400 text-xl font-light">{fmtK(scenarioMonthlyHold)}</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-center">
                  <p className="text-emerald-400/70 text-[11px] uppercase tracking-widest mb-2">Annual Revenue</p>
                  <p className="text-emerald-400 text-2xl font-medium">{fmtK(scenarioAnnualHold)}</p>
                </div>
              </div>

              {/* Pre-built scenarios */}
              <div>
                <p className="text-white/30 text-[11px] uppercase tracking-widest font-normal mb-3">Pre-Built Scenarios</p>
                <div className="space-y-2">
                  <div className="hidden md:grid grid-cols-4 gap-4 px-4 py-2">
                    <p className="text-white/30 text-[10px] uppercase tracking-widest">Scenario</p>
                    <p className="text-white/30 text-[10px] uppercase tracking-widest text-right">Monthly GMV</p>
                    <p className="text-white/30 text-[10px] uppercase tracking-widest text-right">Hold Rev/mo</p>
                    <p className="text-white/30 text-[10px] uppercase tracking-widest text-right">Annual Rev</p>
                  </div>
                  <ScenarioRow
                    label="Current Pace"
                    bookings={Math.max(1, currentMonthProjected)}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={false}
                  />
                  <ScenarioRow
                    label="2× Growth"
                    bookings={Math.max(1, currentMonthProjected * 2)}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={false}
                  />
                  <ScenarioRow
                    label="500 Bookings/mo"
                    bookings={500}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={false}
                  />
                  <ScenarioRow
                    label="1,000 Bookings/mo"
                    bookings={1000}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={true}
                  />
                  <ScenarioRow
                    label="5,000 Bookings/mo"
                    bookings={5000}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={false}
                  />
                  <ScenarioRow
                    label="10,000 Bookings/mo"
                    bookings={10000}
                    avgGMV={scenarioAvgGMV}
                    holdRate={scenarioHoldRate}
                    isHighlight={false}
                  />
                </div>
              </div>
            </motion.div>

            {/* ── Capital Carry ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.43 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw className="w-4 h-4 text-brand-400" />
                <h2 className="text-lg font-light text-white tracking-tight">Capital Carry & Revolving Structure</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-7">
                How long capital stays deployed per booking, how much is always in motion, and what you earn while it revolves.
              </p>

              {/* Per-booking lifecycle */}
              <div className="mb-6 p-6 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-5">Per-Booking Economics (Dec 2025+ averages)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-white text-xl font-light">${fmt(recentAvgBaseFare, 0)}</p>
                    <p className="text-white/40 text-xs font-normal mt-1">capital deployed</p>
                    <p className="text-white/25 text-[10px] mt-0.5">avg base fare / booking</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white text-xl font-light">{avgWindowDays} days</p>
                    <p className="text-white/40 text-xs font-normal mt-1">avg hold period</p>
                    <p className="text-white/25 text-[10px] mt-0.5">
                      {hasScheduleData ? `from ${scheduleCount} real payment schedules` : 'estimated — no schedules found'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-emerald-400 text-xl font-light">${fmt(recentAvgHoldFee, 0)}</p>
                    <p className="text-white/40 text-xs font-normal mt-1">hold fee earned</p>
                    <p className="text-white/25 text-[10px] mt-0.5">profit per booking</p>
                  </div>
                  <div className="text-center">
                    <p className="text-brand-400 text-xl font-light">{fmt(perBookingYield * 100, 1)}%</p>
                    <p className="text-white/40 text-xs font-normal mt-1">yield per booking</p>
                    <p className="text-white/25 text-[10px] mt-0.5">hold fee ÷ capital deployed</p>
                  </div>
                </div>

                {/* Timeline visualization */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] text-white/30 mb-1.5">
                    <span>Booking date</span>
                    <span>Final installment (~{avgWindowDays}d)</span>
                    <span>Departure</span>
                  </div>
                  <div className="relative h-7 rounded-lg overflow-hidden bg-white/5">
                    {/* Capital outstanding bar */}
                    <div
                      className="absolute left-0 top-0 h-full rounded-lg bg-gradient-to-r from-brand-600/50 to-brand-500/30 flex items-center px-3"
                      style={{ width: `${Math.min(95, (avgWindowDays / (avgWindowDays + 14)) * 100)}%` }}
                    >
                      <span className="text-brand-300 text-[10px] font-normal">Capital revolving</span>
                    </div>
                    {/* Hold fee label */}
                    <div className="absolute right-0 top-0 h-full flex items-center px-3">
                      <span className="text-emerald-400 text-[10px] font-normal">+{fmt(perBookingYield * 100, 1)}%</span>
                    </div>
                  </div>
                  <p className="text-white/20 text-[10px] mt-1.5">
                    Capital is returned in installments — the hold fee is your spread for providing it.
                  </p>
                </div>
              </div>

              {/* Revolving capital at current pace */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-5 rounded-2xl border border-brand-500/20 bg-brand-500/5 text-center">
                  <p className="text-brand-400/70 text-[11px] uppercase tracking-widest mb-2">Always Revolving</p>
                  <p className="text-white text-2xl font-light">{fmtK(revolvingCapital)}</p>
                  <p className="text-white/35 text-xs mt-1">at current booking pace</p>
                </div>
                <div className="p-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 text-center">
                  <p className="text-emerald-400/70 text-[11px] uppercase tracking-widest mb-2">Earned While Revolving</p>
                  <p className="text-emerald-400 text-2xl font-light">{fmtK(monthlyHoldAtPace)}<span className="text-base">/mo</span></p>
                  <p className="text-white/35 text-xs mt-1">hold fees at current pace</p>
                </div>
                <div className="p-5 rounded-2xl border border-brand-500/20 bg-brand-500/5 text-center">
                  <p className="text-brand-400/70 text-[11px] uppercase tracking-widest mb-2">Annualised Yield</p>
                  <p className="text-brand-400 text-2xl font-light">{fmt(annualYield * 100, 1)}%</p>
                  <p className="text-white/35 text-xs mt-1">on revolving capital (assumes full deployment)</p>
                </div>
              </div>

              {/* Capital sizing tool */}
              <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-1">Capital Sizing Tool</p>
                <p className="text-white/30 text-xs font-normal mb-1">Enter how much you want to deploy — see what you can support and what you earn.</p>
                <p className="text-white/20 text-[10px] font-normal mb-5">
                  No minimum required. We suggest a <span className="text-brand-400/70">$30K float</span> as a practical starting point for meaningful monthly yield — but you choose what to commit.
                  Uninvested capital (not currently backing an active installment) can be withdrawn at any time.
                </p>

                <div className="mb-5">
                  <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Capital to Deploy</label>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-white/40 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fmtInput(capitalAmount)}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value.replace(/,/g, '')) || 1000;
                        setCapitalAmount(Math.max(1000, raw));
                      }}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                    />
                  </div>
                  <input
                    type="range"
                    min={10000}
                    max={5000000}
                    step={10000}
                    value={capitalAmount}
                    onChange={(e) => setCapitalAmount(parseInt(e.target.value))}
                    className="w-full accent-brand-500"
                  />
                  <div className="flex justify-between text-white/20 text-[10px] mt-1">
                    <span>$10K</span><span>$500K</span><span>$1M</span><span>$2.5M</span><span>$5M</span>
                  </div>
                </div>

                {(() => {
                  // How many bookings can this capital support simultaneously?
                  // Uses net capital at risk (post-deposit) so the revolving estimate is accurate.
                  const bookingsPerMonth = avgNetCapitalAtRisk > 0 && avgWindowDays > 0
                    ? capitalAmount / avgNetCapitalAtRisk / (avgWindowDays / 30)
                    : 0;
                  const monthlyIncome = bookingsPerMonth * recentAvgHoldFee;
                  const annualIncome = monthlyIncome * 12;
                  const impliedYield = capitalAmount > 0 ? annualIncome / capitalAmount : 0;

                  const rows = [
                    { label: 'Bookings supported / month', value: Math.round(bookingsPerMonth).toLocaleString(), note: `theoretical max — assumes full deployment, ${avgWindowDays}-day avg hold` },
                    { label: 'Monthly hold fee income', value: `$${fmt(monthlyIncome)}`, note: `at $${fmt(recentAvgHoldFee, 0)} avg per booking (Dec 2025+)`, accent: 'emerald' },
                    { label: 'Annual hold fee income', value: `$${fmt(annualIncome)}`, note: 'steady-state run rate', accent: 'emerald' },
                    { label: 'Annual yield on capital', value: `${fmt(impliedYield * 100, 1)}%`, note: 'hold fees ÷ capital (theoretical max, assumes full deployment)', accent: 'amber' },
                  ];

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {rows.map((r) => (
                        <div
                          key={r.label}
                          className={`p-4 rounded-xl border text-center ${
                            r.accent === 'emerald' ? 'border-emerald-500/20 bg-emerald-500/5' :
                            r.accent === 'amber' ? 'border-brand-500/20 bg-brand-500/5' :
                            'border-white/8 bg-white/[0.02]'
                          }`}
                        >
                          <p className={`text-xl font-light mb-1 ${
                            r.accent === 'emerald' ? 'text-emerald-400' :
                            r.accent === 'amber' ? 'text-brand-400' :
                            'text-white'
                          }`}>{r.value}</p>
                          <p className="text-white/50 text-[11px] font-normal leading-snug">{r.label}</p>
                          <p className="text-white/25 text-[10px] mt-0.5">{r.note}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </motion.div>

            {/* ── How It Works ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mb-10"
            >
              <h2 className="text-lg font-light text-white tracking-tight mb-5">How the Investment Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    step: '01',
                    title: 'Capital Float',
                    body: 'Investor provides a revolving capital float. As customers book flights, capital is drawn from the pool to cover installments. Capital not currently deployed on an active booking can be withdrawn at any time.',
                  },
                  {
                    step: '02',
                    title: 'Hold Fee Collection',
                    body: 'Each installment booking carries a 15% Hold Fee on top of (base fare + service fee). The investor earns this fee in full as the capital provider.',
                  },
                  {
                    step: '03',
                    title: 'Revenue Stream',
                    body: 'Hold fees are collected across the installment schedule. As booking volume grows, revenue scales proportionally — no additional infrastructure required.',
                  },
                ].map((item) => (
                  <div key={item.step} className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <p className="text-white/20 text-xs font-mono mb-3">{item.step}</p>
                    <p className="text-white text-sm font-normal mb-2">{item.title}</p>
                    <p className="text-white/65 text-sm font-normal leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* ── Risk Disclosure ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48 }}
              className="mb-10 p-5 rounded-2xl border border-brand-500/15 bg-brand-500/[0.04]"
            >
              <p className="text-brand-400/70 text-[10px] uppercase tracking-widest font-normal mb-4">Key Risks to Understand</p>

              {/* Default rate stat — real data front and centre */}
              <div className="grid grid-cols-2 gap-3 mb-5 p-4 rounded-xl border border-brand-500/10 bg-brand-500/5">
                <div className="text-center">
                  <p className={`text-2xl font-light ${defaultRate === 0 ? 'text-emerald-400' : defaultRate < 0.05 ? 'text-emerald-400' : 'text-brand-400'}`}>
                    {installmentBookings.length > 0 ? `${fmt(defaultRate * 100, 1)}%` : '—'}
                  </p>
                  <p className="text-white/40 text-[11px] mt-1">historical default rate</p>
                  <p className="text-white/25 text-[10px] mt-0.5">installment bookings marked defaulted</p>
                </div>
                <div className="text-center">
                  <p className="text-white text-2xl font-light">{defaultCount}</p>
                  <p className="text-white/40 text-[11px] mt-1">total defaults</p>
                  <p className="text-white/25 text-[10px] mt-0.5">out of {installmentBookings.length} installment bookings</p>
                </div>
              </div>

              {/* Default rate by deposit tier */}
              {depositTierRows.length > 0 && (
                <div className="mb-5 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
                  <p className="text-white/30 text-[10px] uppercase tracking-widest font-normal mb-1">Default Rate by Upfront Deposit %</p>
                  <p className="text-white/20 text-[10px] font-normal mb-4">
                    Higher upfront deposits reduce the investor's capital at risk per booking. This table shows whether deposit size correlates with lower defaults in real data.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Deposit %</th>
                          <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Bookings</th>
                          <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Defaults</th>
                          <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Default Rate</th>
                          <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Capital Protected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depositTierRows.map(({ pct, total, defaulted, rate }) => {
                          const safest = depositTierRows.length > 1 && rate === Math.min(...depositTierRows.map(r => r.rate));
                          const isLive = LIVE_DEPOSIT_TIERS.has(pct);
                          const rateColor = rate === 0 ? 'text-emerald-400' : rate < 0.05 ? 'text-emerald-400/80' : rate < 0.15 ? 'text-brand-400' : 'text-red-400';
                          return (
                            <tr key={pct} className={`border-b border-white/[0.04] ${safest ? 'bg-emerald-500/5' : ''} ${!isLive ? 'opacity-50' : ''}`}>
                              <td className="py-2.5 font-normal">
                                <span className={isLive ? 'text-white' : 'text-white/50 line-through'}>{pct}% upfront</span>
                                {isLive && <span className="ml-2 px-1.5 py-0.5 rounded text-[8px] bg-brand-500/15 text-brand-400/80 border border-brand-500/20 uppercase tracking-widest">live</span>}
                                {!isLive && <span className="ml-2 px-1.5 py-0.5 rounded text-[8px] bg-white/5 text-white/25 border border-white/10 uppercase tracking-widest">discontinued</span>}
                                {safest && isLive && <span className="ml-1.5 text-emerald-400/70 text-[9px] uppercase tracking-widest">· safest</span>}
                              </td>
                              <td className="py-2.5 text-right text-white/50">{total}</td>
                              <td className="py-2.5 text-right text-white/50">{defaulted}</td>
                              <td className={`py-2.5 text-right font-medium ${rateColor}`}>
                                {rate === 0 ? '0%' : `${fmt(rate * 100, 1)}%`}
                              </td>
                              <td className="py-2.5 text-right text-white/40">
                                {pct}% of avg ${fmt(recentAvgBaseFare, 0)} = <span className="text-white/60">${fmt(recentAvgBaseFare * pct / 100, 0)}</span> covered
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-white/20 text-[10px] font-normal mt-3 leading-relaxed">
                    "Capital Protected" shows how much of the avg base fare is recovered upfront at each deposit tier — reducing the investor's net exposure if a booking defaults after the deposit is collected.
                    Currently live tiers: <span className="text-brand-400/60">5%, 10%, 20%, 30%, 40%, 50%</span> (5% / 10% offered on Defera Capital trips with 90+ day lead time). Discontinued tiers are historical data only.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-white/85 text-xs font-normal mb-1">Customer Non-Payment</p>
                  <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                    If a customer misses installments or cancels before completing payment, the unrecovered capital is a loss absorbed by the investor — it is their money at risk. Defera also loses the hold fee and service fee it would have earned on the booking. Both parties take a hit on defaults, but the principal exposure sits with the investor.
                  </p>
                </div>
                <div>
                  <p className="text-white/85 text-xs font-normal mb-1">Capital Utilisation</p>
                  <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                    Yield figures assume the capital pool is fully deployed at all times. At lower booking volumes, a portion of capital may sit idle between bookings, reducing effective yield. Withdrawing idle capital mitigates this — uninvested funds are not locked.
                  </p>
                </div>
                <div>
                  <p className="text-white/85 text-xs font-normal mb-1">Volume Risk</p>
                  <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                    Hold fee income scales with booking volume. If monthly bookings decline, income declines proportionally. All projections on this page are directional models — not guarantees of future performance.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* ── Pipeline & Cart Data ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {/* Service Fee Breakdown */}
              <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-1">Platform Service Fees</p>
                <p className="text-white/25 text-[10px] font-normal mb-4">Defera's operating revenue — not the investor's earnings. Investor earns hold fees only.</p>
                <p className="text-white text-2xl font-light mb-1">{fmtK(totalServiceFees)}</p>
                <p className="text-white/40 text-xs font-normal mb-4">total service fees collected by Defera</p>
                <div className="space-y-2">
                  {Object.entries(methodCounts).map(([pm, count]) => (
                    <div key={pm} className="flex items-center justify-between">
                      <span className="text-white/50 text-xs font-normal capitalize">{pm.replace(/_/g, ' ')}</span>
                      <span className="text-white/70 text-xs font-normal">{count} bookings</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking Velocity */}
              <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-4">Booking Velocity</p>
                {last12.length > 0 ? (
                  <>
                    <p className="text-white text-2xl font-light mb-1">
                      {last12.reduce((s, m) => s + m.bookings, 0).toLocaleString()}
                    </p>
                    <p className="text-white/40 text-xs font-normal mb-4">bookings in last {last12.length} tracked months</p>
                    <div className="space-y-2">
                      {last12.slice(-4).map((m) => (
                        <div key={m.label} className="flex items-center justify-between">
                          <span className="text-white/50 text-xs font-normal">{m.label}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-white/70 text-xs font-normal">{m.bookings} bookings</span>
                            <span className="text-emerald-400 text-xs font-normal">{fmtK(m.holdFees)} hold</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-white/30 text-sm font-normal">No booking history available yet.</p>
                )}
              </div>
            </motion.div>

            {/* ── Marketing Co-Investment ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.56 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-brand-400" />
                <h2 className="text-lg font-light text-white tracking-tight">Marketing Co-Investment</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-2">
                The two periods below are pulled from real booking data. June–November 2025 had ~$2K/mo in paid ads running. December 2025 onward has been minimal TikTok influencer spend (~$100–$300/mo) with no paid ads. Both periods are shown as reference points — not guarantees — for what marketing spend drives in volume.
              </p>
              <p className="text-white/25 text-xs font-normal mb-7">
                Note: the low-spend period is still recent. The numbers may continue to settle as recurring customers and referrals compound.
              </p>

              {/* Spend period context */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                  <p className="text-white/30 text-[10px] uppercase tracking-widest font-normal mb-3">Jun–Nov 2025 · ~$2K/mo in ads</p>
                  {hasAdPeriodData ? (
                    <>
                      <p className="text-white text-2xl font-light">{fmtK(adPeriodAvg * avgGMV)}<span className="text-base text-white/40"> /mo</span></p>
                      <p className="text-white/40 text-xs font-normal mt-1">avg monthly volume while paid ads were running</p>
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-white/25 text-[11px]">
                          {fmt(adPeriodAvg, 1)} bookings/mo avg
                          {liftIsMeaningful ? ` · implied $${fmt(costPerBookingAds, 0)} per booking from ads` : ' · gap vs low-spend period too small to derive cost-per-booking'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-white/30 text-2xl font-light">—</p>
                      <p className="text-white/40 text-xs font-normal mt-1">booking data for this period not yet in range</p>
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-white/25 text-[11px]">Jun–Nov 2025 bookings will appear here once loaded</p>
                      </div>
                    </>
                  )}
                </div>
                <div className="p-5 rounded-2xl border border-brand-500/20 bg-brand-500/5">
                  <p className="text-brand-400/70 text-[10px] uppercase tracking-widest font-normal mb-3">Dec 2025–Now · ~$100–$300/mo TikTok only</p>
                  <p className="text-white text-2xl font-light">{fmtK(organicAvg * avgGMV)}<span className="text-base text-white/40"> /mo</span></p>
                  <p className="text-white/40 text-xs font-normal mt-1">avg monthly volume since ads stopped — recurring customers, referrals &amp; light TikTok</p>
                  <div className="mt-3 pt-3 border-t border-brand-500/10">
                    <p className="text-brand-400/50 text-[11px]">{fmt(organicAvg, 1)} bookings/mo avg · what the platform sustains at minimal spend</p>
                  </div>
                </div>
              </div>

              {/* What the gap means — plain English */}
              <div className="p-4 rounded-xl border border-white/5 bg-white/[0.015] mb-6">
                {hasAdPeriodData && liftIsMeaningful ? (
                  <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                    <span className="text-white/70">What the gap tells us:</span> During the ad period (Jun–Nov 2025), bookings averaged {fmt(adPeriodAvg, 1)}/mo. Since ads stopped (Dec 2025–now), they've averaged {fmt(organicAvg, 1)}/mo. The difference — roughly {fmt(adIncrementalBookings, 1)} extra bookings/mo — is what ~$2K/mo in spend historically drove, implying a cost of <span className="text-white/70">${fmt(costPerBookingAds, 0)} per incremental booking</span>. TikTok influencer spend is modeled at 40% better efficiency — <span className="text-brand-300">${fmt(costPerBookingTikTok, 0)}/booking</span>. Directional estimates only.
                  </p>
                ) : (
                  <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                    <span className="text-white/70">Not enough contrast between periods to calculate a reliable cost-per-booking yet.</span> The ad period (Jun–Nov 2025) and low-spend period (Dec 2025–now) need a clear difference in booking volume for that math to be meaningful. The marketing scenario below uses a conservative industry estimate of <span className="text-white/70">$150/booking</span> for TikTok travel influencers until real data supports a better figure.
                  </p>
                )}
              </div>

              {/* Marketing budget selector */}
              <div className="p-6 rounded-2xl border border-brand-500/20 bg-brand-500/5 mb-6">
                <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-1">Monthly TikTok Marketing Budget</p>
                <p className="text-white/30 text-xs font-normal mb-5">
                  Starting from today's baseline (zero paid ads), this models what adding TikTok influencer spend would drive on top. The current baseline volume stays regardless — marketing only adds to it.
                </p>

                {/* ── Organic traffic breakdown — what's driving volume WITHOUT new ads ── */}
                <div className="mb-6 p-5 rounded-2xl border border-white/5 bg-white/[0.02]">
                  <p className="text-white/25 text-[10px] uppercase tracking-widest mb-4">
                    Where current volume comes from — Nov 2025 to now · {relevantBookings.length} bookings
                    {users.length > 0 ? ` · ${users.length} users matched` : ''}
                  </p>

                  {organicChannelRows.length > 0 ? (
                    <div className="space-y-2.5">
                      {organicChannelRows.map(({ channel, count, gmv, pct }) => {
                        const isRecurring = channel === 'recurring customer';
                        const isFriend = channel === 'friend referral';
                        const isReddit = channel === 'reddit';
                        const isTikTok = channel === 'tiktok';
                        const color = isRecurring
                          ? 'bg-emerald-500/60'
                          : isFriend
                          ? 'bg-brand-500/60'
                          : isReddit
                          ? 'bg-orange-500/60'
                          : isTikTok
                          ? 'bg-pink-500/60'
                          : 'bg-white/20';
                        const textColor = isRecurring
                          ? 'text-emerald-400'
                          : isFriend
                          ? 'text-brand-400'
                          : isReddit
                          ? 'text-orange-400'
                          : isTikTok
                          ? 'text-pink-400'
                          : 'text-white/50';
                        const label = isRecurring
                          ? 'Recurring customers (repeat bookings at $0 cost)'
                          : isFriend
                          ? 'Friend / referral (word of mouth)'
                          : isReddit
                          ? 'Reddit (organic community)'
                          : isTikTok
                          ? 'TikTok (influencer content)'
                          : referralSourceLabel(channel);

                        return (
                          <div key={channel}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-normal ${textColor}`}>{label}</span>
                              <span className="text-white/40 text-xs">
                                {count} bookings · {fmtK(gmv)} GMV · {fmt(pct * 100, 0)}%
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-700 ${color}`}
                                style={{ width: `${pct * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-white/20 text-xs">Referral source data loading…</p>
                  )}

                  <p className="text-white/20 text-[10px] font-normal mt-4 leading-relaxed">
                    {recurringBookingCount > 0 && (
                      <>
                        <strong className="text-emerald-400/70">{fmt(recurringPct * 100, 0)}% of all volume since Nov 2025</strong> comes from customers who booked more than once — that's truly free revenue that grows regardless of ad spend.{' '}
                      </>
                    )}
                    The remaining first-time bookings are split across referral channels above. Marketing spend targets new first-time customers; organic channels continue in parallel.
                  </p>
                </div>

                {/* Customer repeat behaviour — derived from all bookings since Nov 2025 */}
                <div className="grid grid-cols-4 gap-3 mb-6 p-4 rounded-xl border border-white/5 bg-white/[0.015]">
                  <div className="text-center">
                    <p className="text-white text-lg font-light">{uniqueCustomers > 0 ? uniqueCustomers.toLocaleString() : '—'}</p>
                    <p className="text-white/40 text-[11px] mt-1">unique customers</p>
                    <p className="text-white/25 text-[10px]">since Nov 2025</p>
                  </div>
                  <div className="text-center">
                    <p className="text-brand-400 text-lg font-light">{fmt(avgBookingsPerCustomer, 2)}×</p>
                    <p className="text-white/40 text-[11px] mt-1">avg bookings/customer</p>
                    <p className="text-white/25 text-[10px]">{fmt(repeatRate * 100, 0)}% booked more than once</p>
                  </div>
                  <div className="text-center">
                    <p className="text-brand-300 text-lg font-light">{fmt(monthlyRepeatRate * 100, 2)}%</p>
                    <p className="text-white/40 text-[11px] mt-1">monthly repeat rate</p>
                    <p className="text-white/25 text-[10px]">rebookings per customer/mo</p>
                  </div>
                  <div className="text-center">
                    <p className="text-emerald-400 text-lg font-light">${fmt(customerLTV, 0)}</p>
                    <p className="text-white/40 text-[11px] mt-1">hold fee LTV</p>
                    <p className="text-white/25 text-[10px]">per customer, lifetime</p>
                  </div>
                </div>

                <div className="border-t border-white/5 mb-5 pt-5">
                  <p className="text-white/40 text-[11px] uppercase tracking-widest font-normal mb-1">Adjust Budget — See the Projection Update Below</p>
                </div>

                <div className="mb-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-white/40 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fmtInput(marketingBudget)}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value.replace(/,/g, '')) || 0;
                        setMarketingBudget(Math.max(0, raw));
                      }}
                      className="flex-1 bg-white/5 border border-brand-500/20 rounded-xl px-4 py-3 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                    />
                    <span className="text-white/40 text-sm whitespace-nowrap">/ month</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={10000}
                    step={100}
                    value={marketingBudget}
                    onChange={(e) => setMarketingBudget(parseInt(e.target.value))}
                    className="w-full accent-brand-500"
                  />
                  <div className="flex justify-between text-white/20 text-[10px] mt-1">
                    <span>$0</span><span>$1K</span><span>$2K</span><span>$5K</span><span>$10K</span>
                  </div>
                </div>

                {/* Quick presets */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {[200, 500, 1000, 2000, 5000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setMarketingBudget(v)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-normal transition-all ${
                        marketingBudget === v
                          ? 'bg-brand-500/25 border border-brand-500/50 text-brand-300'
                          : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
                      }`}
                    >
                      ${v >= 1000 ? `${v/1000}K` : v}/mo
                      {v === 200 && <span className="text-brand-400/60 ml-1">(current)</span>}
                      {v === 2000 && <span className="text-white/30 ml-1">(peak)</span>}
                    </button>
                  ))}
                </div>

                {/* Cost-per-booking assumption — this is the key lever */}
                <div className="mb-6 p-4 rounded-xl border border-brand-500/15 bg-brand-500/5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-brand-300/70 text-[11px] uppercase tracking-widest font-normal">Cost per booking (CPB)</p>
                      <p className="text-white/30 text-xs font-normal mt-0.5">
                        How much it costs to acquire one new customer booking via TikTok.
                        {costPerBookingTikTok !== null
                          ? ` Derived from ad-period vs low-spend-period lift: $${fmt(costPerBookingTikTok, 0)}/booking (assumes ${TIKTOK_EFFICIENCY}× TikTok efficiency vs broad ads — directional estimate).`
                          : ` No clear ad-period lift detected — using your estimate below.`}
                      </p>
                    </div>
                    {cpbOverride !== null && (
                      <button
                        onClick={() => setCpbOverride(null)}
                        className="text-white/25 text-[10px] hover:text-white/50 transition-colors whitespace-nowrap ml-3 mt-0.5"
                      >
                        reset to {costPerBookingTikTok !== null ? `data ($${Math.round(costPerBookingTikTok)})` : 'default'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-white/40 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fmtInput(cpbOverride ?? (costPerBookingTikTok ?? 100))}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value.replace(/,/g, '')) || 0;
                        setCpbOverride(Math.max(1, raw));
                      }}
                      className="flex-1 bg-white/5 border border-brand-500/20 rounded-xl px-4 py-2.5 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                    />
                    <span className="text-white/40 text-sm whitespace-nowrap">per booking</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[50, 75, 100, 150, 200].map((v) => {
                      const activeCPB = cpbOverride ?? (costPerBookingTikTok ?? 100);
                      return (
                        <button
                          key={v}
                          onClick={() => setCpbOverride(v)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-normal transition-all ${
                            Math.round(activeCPB) === v
                              ? 'bg-brand-500/25 border border-brand-500/50 text-brand-300'
                              : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/60'
                          }`}
                        >
                          ${v}
                          {v === 75 && <span className="text-white/30 ml-1">(influencer avg)</span>}
                          {v === 150 && <span className="text-white/30 ml-1">(conservative)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {(() => {
                    const activeCPB = cpbOverride ?? (costPerBookingTikTok ?? 100);
                    const ratio = recentAvgHoldFee / activeCPB;
                    return (
                      <p className={`text-[10px] font-normal mt-2 ${ratio >= 1 ? 'text-emerald-400/70' : 'text-brand-400/70'}`}>
                        {ratio >= 1
                          ? `✓ At $${Math.round(activeCPB)}/booking: hold fee ($${fmt(recentAvgHoldFee, 0)}) covers ${fmt(ratio, 2)}× cost — marketing is profitable from day 1. Net grows with every dollar spent.`
                          : `At $${Math.round(activeCPB)}/booking: hold fee ($${fmt(recentAvgHoldFee, 0)}) covers ${fmt(ratio * 100, 0)}% of cost — recurring bookings make up the difference over time.`}
                      </p>
                    );
                  })()}
                </div>

                {(() => {
                  const effectiveCPB = cpbOverride ?? costPerBookingTikTok ?? 100;
                  const newCustsPerMonth = marketingBudget / effectiveCPB;

                  // ── 12-month layered projection ──────────────────────────────
                  // Three booking layers each month:
                  //   1. organicAvg       — existing baseline (recurring + referrals already in the platform)
                  //   2. newFirst         — fresh first-time customers acquired this month via marketing
                  //   3. repeatFromMktg   — repeat bookings from ALL prior marketing cohorts compounding
                  //
                  // Layer 3 grows proportionally with marketing spend — double the spend,
                  // double the pool size, double the recurring volume by month 12.
                  // Layer 1 (organic) is conservative/fixed; it does NOT shrink when marketing is added.
                  const MONTHS = 12;
                  const projection = Array.from({ length: MONTHS }, (_, i) => {
                    const M = i + 1;
                    const organicLayer  = organicAvg;
                    const newFirstLayer = newCustsPerMonth;
                    // Each prior month's cohort of newCustsPerMonth contributes monthlyRepeatRate repeats/mo
                    // By month M: sum of (M-1) cohorts each at monthlyRepeatRate
                    const repeatLayer   = (M - 1) * newCustsPerMonth * monthlyRepeatRate;
                    const totalBookings = organicLayer + newFirstLayer + repeatLayer;
                    const holdFees = totalBookings * recentAvgHoldFee;
                    return {
                      M,
                      organicLayer,
                      newFirstLayer,
                      repeatLayer,
                      totalBookings,
                      holdFees,
                      net: holdFees - marketingBudget,
                      capitalRev: totalBookings * avgNetCapitalAtRisk * (avgWindowDays / 30),
                      gmv: totalBookings * recentAvgGMV,
                    };
                  });

                  const avgNet        = projection.reduce((s, r) => s + r.net, 0) / MONTHS;
                  const avgHoldFees12 = projection.reduce((s, r) => s + r.holdFees, 0) / MONTHS;
                  const avgGMV12      = projection.reduce((s, r) => s + r.gmv, 0) / MONTHS;
                  const avgCapital12  = projection.reduce((s, r) => s + r.capitalRev, 0) / MONTHS;

                  const milestones = [1, 3, 6, 12];
                  const m12 = projection[11];
                  // Max total for stacked bar scaling
                  const maxTotal = m12.totalBookings;

                  return (
                    <>
                      {/* Layer legend */}
                      <div className="flex items-center gap-5 mb-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-sm bg-brand-500/50" />
                          <span className="text-white/40 text-[10px]">Organic baseline</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-sm bg-brand-500/60" />
                          <span className="text-white/40 text-[10px]">New (first-time, from marketing)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500/60" />
                          <span className="text-white/40 text-[10px]">Recurring (from past mktg cohorts)</span>
                        </div>
                      </div>

                      {/* Stacked bar visual for milestone months */}
                      <div className="mb-4 space-y-2">
                        <p className="text-white/25 text-[10px] uppercase tracking-widest mb-2">
                          Booking layers by month — recurring grows proportionally with marketing spend
                        </p>
                        {projection.filter(r => milestones.includes(r.M)).map((row) => (
                          <div key={row.M} className="flex items-center gap-3">
                            <span className="text-white/25 text-[10px] w-14 shrink-0">Mo {row.M}</span>
                            <div className="flex-1 h-5 rounded-lg overflow-hidden bg-white/5 flex">
                              <div
                                className="h-full bg-brand-500/40 flex items-center justify-center shrink-0"
                                style={{ width: `${(row.organicLayer / maxTotal) * 100}%` }}
                              >
                                {row.organicLayer / row.totalBookings > 0.25 && (
                                  <span className="text-[8px] text-brand-200/60 px-1">{Math.round(row.organicLayer)}</span>
                                )}
                              </div>
                              <div
                                className="h-full bg-brand-500/50 flex items-center justify-center shrink-0"
                                style={{ width: `${(row.newFirstLayer / maxTotal) * 100}%` }}
                              >
                                {row.newFirstLayer / row.totalBookings > 0.1 && (
                                  <span className="text-[8px] text-brand-200/80 px-1">{Math.round(row.newFirstLayer)}</span>
                                )}
                              </div>
                              <div
                                className="h-full bg-emerald-500/50 flex items-center justify-center shrink-0 transition-all duration-700"
                                style={{ width: `${(row.repeatLayer / maxTotal) * 100}%` }}
                              >
                                {row.repeatLayer > 0.5 && row.repeatLayer / row.totalBookings > 0.05 && (
                                  <span className="text-[8px] text-emerald-200/80 px-1">{fmt(row.repeatLayer, 1)}</span>
                                )}
                              </div>
                            </div>
                            <span className="text-white/50 text-[10px] w-20 text-right shrink-0">
                              {fmtK(row.holdFees)} fees
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Detailed milestone table */}
                      <div className="mb-5">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/5">
                                <th className="text-left text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Month</th>
                                <th className="text-right text-brand-400/50 text-[10px] uppercase tracking-widest pb-2 font-normal">Organic</th>
                                <th className="text-right text-brand-400/60 text-[10px] uppercase tracking-widest pb-2 font-normal">New</th>
                                <th className="text-right text-emerald-400/60 text-[10px] uppercase tracking-widest pb-2 font-normal">Repeat Bookings</th>
                                <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Volume</th>
                                <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Hold Fees</th>
                                <th className="text-right text-white/25 text-[10px] uppercase tracking-widest pb-2 font-normal">Net/mo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {projection.filter(r => milestones.includes(r.M)).map((row) => (
                                <tr key={row.M} className={`border-b border-white/[0.04] ${row.M === 12 ? 'text-white' : 'text-white/70'}`}>
                                  <td className="py-2.5 text-white/40 text-xs">
                                    Mo {row.M}
                                    {row.M === 1 && <span className="text-white/20 ml-1">(start)</span>}
                                    {row.M === 12 && <span className="text-brand-400/60 ml-1">(mature)</span>}
                                  </td>
                                  <td className="py-2.5 text-right text-brand-400/60 text-xs">{Math.round(row.organicLayer)}</td>
                                  <td className="py-2.5 text-right text-brand-300/70 text-xs">{Math.round(row.newFirstLayer)}</td>
                                  <td className="py-2.5 text-right text-emerald-400/80 text-xs font-normal">{fmt(row.repeatLayer, 1)}</td>
                                  <td className="py-2.5 text-right text-xs font-normal">{fmtK(row.gmv)}</td>
                                  <td className="py-2.5 text-right text-emerald-400 text-xs font-normal">{fmtK(row.holdFees)}</td>
                                  <td className={`py-2.5 text-right text-xs font-normal ${row.net >= 0 ? 'text-emerald-400' : 'text-brand-400'}`}>
                                    {row.net >= 0 ? '' : '−'}{fmtK(Math.abs(row.net))}
                                  </td>
                                </tr>
                              ))}
                              {/* 12-month average */}
                              <tr className="border-t border-brand-500/20 bg-brand-500/5">
                                <td className="py-3 text-brand-300/70 text-xs font-normal">Avg mo 1–12</td>
                                <td className="py-3 text-right text-brand-400/50 text-xs">{Math.round(organicAvg)}</td>
                                <td className="py-3 text-right text-brand-300/60 text-xs">{Math.round(newCustsPerMonth)}</td>
                                <td className="py-3 text-right text-emerald-400/70 text-xs font-normal">
                                  {fmt(projection.reduce((s, r) => s + r.repeatLayer, 0) / MONTHS, 1)}
                                </td>
                                <td className="py-3 text-right text-white text-xs font-normal">{fmtK(avgGMV12)}</td>
                                <td className="py-3 text-right text-emerald-400 text-xs font-normal">{fmtK(avgHoldFees12)}</td>
                                <td className={`py-3 text-right text-xs font-normal ${avgNet >= 0 ? 'text-emerald-400' : 'text-brand-400'}`}>
                                  {avgNet >= 0 ? '' : '−'}{fmtK(Math.abs(avgNet))}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-white/20 text-[10px] font-normal mt-3 leading-relaxed">
                          <strong className="text-emerald-400/60">Repeat Bookings grow proportionally with marketing spend.</strong>{' '}
                          If you double the budget, you double new customers/mo, which doubles the pool generating repeat bookings — so the green layer by month 12 is 2× as large. The organic baseline ({Math.round(organicAvg)} bookings/mo) runs in parallel and is not displaced. Repeat rate ({fmt(monthlyRepeatRate * 100, 2)}%/customer/mo) is from {relevantBookings.length} real bookings since Nov 2025.
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>

            </motion.div>

            {/* ── Combined Investment Summary (standalone) ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-brand-400" />
                <h2 className="text-lg font-light text-white tracking-tight">Combined Investment Summary</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-7">
                Your total monthly net across time — hold fees from organic volume plus uplift from marketing co-investment, minus marketing cost.
                Adjust the inputs below or in the Marketing section above; they're the same controls and stay in sync.
              </p>

              {/* Synced controls */}
              <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02] mb-6">
                <p className="text-white/25 text-[10px] uppercase tracking-widest font-medium mb-4">Scenario Inputs — synced with Marketing section above</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Marketing budget */}
                  <div>
                    <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Monthly Marketing Budget</label>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white/40 text-sm">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={fmtInput(marketingBudget)}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value.replace(/,/g, '')) || 0;
                          setMarketingBudget(Math.max(0, raw));
                        }}
                        className="flex-1 bg-white/5 border border-brand-500/20 rounded-xl px-4 py-2.5 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                      />
                      <span className="text-white/40 text-sm whitespace-nowrap">/ month</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={10000}
                      step={100}
                      value={marketingBudget}
                      onChange={(e) => setMarketingBudget(parseInt(e.target.value))}
                      className="w-full accent-brand-500"
                    />
                    <div className="flex justify-between text-white/20 text-[10px] mt-1">
                      <span>$0</span><span>$2.5K</span><span>$5K</span><span>$7.5K</span><span>$10K</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {[0, 500, 1000, 2000, 5000].map((v) => (
                        <button
                          key={v}
                          onClick={() => setMarketingBudget(v)}
                          className={`px-3 py-1 rounded-lg text-[10px] font-normal transition-all ${
                            marketingBudget === v
                              ? 'bg-brand-500/25 border border-brand-500/50 text-brand-300'
                              : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/60'
                          }`}
                        >
                          {v === 0 ? 'No mktg' : `$${v >= 1000 ? `${v/1000}K` : v}/mo`}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* CPB */}
                  <div>
                    <label className="text-white/40 text-[11px] uppercase tracking-widest font-normal block mb-2">Cost Per Booking (CPB)</label>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white/40 text-sm">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={fmtInput(cpbOverride ?? (costPerBookingTikTok ?? 100))}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value.replace(/,/g, '')) || 0;
                          setCpbOverride(Math.max(1, raw));
                        }}
                        className="flex-1 bg-white/5 border border-brand-500/20 rounded-xl px-4 py-2.5 text-white text-base font-normal outline-none focus:border-brand-500/50 transition-colors"
                      />
                      <span className="text-white/40 text-sm whitespace-nowrap">per booking</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {[50, 75, 100, 150, 200].map((v) => {
                        const activeCPB = cpbOverride ?? (costPerBookingTikTok ?? 100);
                        return (
                          <button
                            key={v}
                            onClick={() => setCpbOverride(v)}
                            className={`px-2.5 py-1 rounded-lg text-[10px] font-normal transition-all ${
                              Math.round(activeCPB) === v
                                ? 'bg-brand-500/25 border border-brand-500/50 text-brand-300'
                                : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/60'
                            }`}
                          >
                            ${v}
                            {v === 75 && <span className="text-white/30 ml-1">(influencer avg)</span>}
                            {v === 150 && <span className="text-white/30 ml-1">(conservative)</span>}
                          </button>
                        );
                      })}
                      {cpbOverride !== null && (
                        <button
                          onClick={() => setCpbOverride(null)}
                          className="px-2.5 py-1 rounded-lg text-[10px] font-normal text-white/25 bg-white/5 border border-white/10 hover:text-white/50 transition-colors"
                        >
                          reset to {costPerBookingTikTok !== null ? `data ($${Math.round(costPerBookingTikTok)})` : 'default'}
                        </button>
                      )}
                    </div>
                    <p className="text-white/20 text-[10px] mt-3 font-normal">
                      How much it costs to acquire one booking via TikTok. Lower CPB = marketing is more profitable per dollar.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary content */}
              <div className="p-6 rounded-2xl border border-brand-500/20 bg-gradient-to-br from-brand-950/20 to-black/50">
                {(() => {
                  const effectiveCPB     = cpbOverride ?? costPerBookingTikTok ?? 100;
                  const newCustsPerMonth = marketingBudget / effectiveCPB;

                  const baselineHoldFees = organicAvg * recentAvgHoldFee;

                  const mktgHoldM1  = newCustsPerMonth * recentAvgHoldFee;
                  const repeatM6    = 5  * newCustsPerMonth * monthlyRepeatRate;
                  const repeatM12   = 11 * newCustsPerMonth * monthlyRepeatRate;
                  const repeatM24   = 23 * newCustsPerMonth * monthlyRepeatRate;

                  const mktgHoldM6  = (newCustsPerMonth + repeatM6)  * recentAvgHoldFee;
                  const mktgHoldM12 = (newCustsPerMonth + repeatM12) * recentAvgHoldFee;
                  const mktgHoldM24 = (newCustsPerMonth + repeatM24) * recentAvgHoldFee;

                  const totalNetM1  = baselineHoldFees + mktgHoldM1  - marketingBudget;
                  const totalNetM6  = baselineHoldFees + mktgHoldM6  - marketingBudget;
                  const totalNetM12 = baselineHoldFees + mktgHoldM12 - marketingBudget;
                  const totalNetM24 = baselineHoldFees + mktgHoldM24 - marketingBudget;
                  const capitalM12  = (organicAvg + newCustsPerMonth + repeatM12) * avgNetCapitalAtRisk * (avgWindowDays / 30);

                  const holdFeePerCPB = effectiveCPB > 0 ? recentAvgHoldFee / effectiveCPB : 0;
                  const mktgBreakEvenMonths = recentAvgHoldFee > 0 && monthlyRepeatRate > 0
                    ? Math.ceil((effectiveCPB - recentAvgHoldFee) / (recentAvgHoldFee * monthlyRepeatRate))
                    : null;

                  const pastAdSpendTotal = 7000;
                  const baselineAsROI = pastAdSpendTotal > 0 ? (baselineHoldFees * 12) / pastAdSpendTotal : 0;

                  const netColor = (v) => v >= 0 ? 'text-emerald-400' : 'text-brand-400';
                  const fmtNet   = (v) => `${v >= 0 ? '' : '−'}${fmtK(Math.abs(v))}`;

                  return (
                    <>
                      {/* Proof callout */}
                      <div className="mb-5 p-4 rounded-xl border border-emerald-500/15 bg-emerald-500/5">
                        <p className="text-emerald-400/70 text-[10px] uppercase tracking-widest mb-2">Proof that marketing compounds — not cancels</p>
                        <p className="text-white/70 text-sm font-normal leading-relaxed mb-3">
                          The <span className="text-brand-400">{fmtK(baselineHoldFees)}/mo organic baseline</span> you see today is the compounded result of <span className="text-white/60">~$7,000 in ad spend</span> since Nov 2025. Those customers never stopped booking. That's a{' '}
                          <span className="text-emerald-400 font-medium">{fmt(baselineAsROI, 1)}× annualised return</span> on the original spend — still paying out months after ads stopped.
                        </p>
                        <p className="text-white/35 text-xs font-normal">
                          Every marketing dollar spent today is building the <em>next</em> organic baseline. Month 12 of new marketing looks like today's organic floor — not month 1.
                        </p>
                      </div>

                      {/* Unit economics */}
                      <div className="grid grid-cols-3 gap-3 mb-5 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
                        <div className="text-center">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Hold Fee / Booking</p>
                          <p className="text-white text-xl font-light">${fmt(recentAvgHoldFee, 0)}</p>
                          <p className="text-white/20 text-[10px] mt-1">earned per booking (Dec 2025+ avg)</p>
                        </div>
                        <div className="text-center">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Cost / New Customer</p>
                          <p className={`text-xl font-light ${holdFeePerCPB >= 1 ? 'text-emerald-400' : 'text-brand-400'}`}>
                            ${fmt(effectiveCPB, 0)}
                          </p>
                          <p className="text-white/20 text-[10px] mt-1">
                            {holdFeePerCPB >= 1
                              ? `✓ first booking covers ${fmt(holdFeePerCPB, 2)}× cost — profitable day 1`
                              : `first booking covers ${fmt(holdFeePerCPB * 100, 0)}% of cost`}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">
                            {mktgBreakEvenMonths !== null && mktgBreakEvenMonths > 0 ? 'Break-even via recurring' : 'Status'}
                          </p>
                          <p className={`text-xl font-light ${holdFeePerCPB >= 1 ? 'text-emerald-400' : 'text-white'}`}>
                            {holdFeePerCPB >= 1
                              ? 'Day 1'
                              : mktgBreakEvenMonths !== null && mktgBreakEvenMonths > 0
                              ? `~${mktgBreakEvenMonths} mo`
                              : '—'}
                          </p>
                          <p className="text-white/20 text-[10px] mt-1">
                            {holdFeePerCPB >= 1
                              ? 'every repeat booking is pure upside'
                              : mktgBreakEvenMonths !== null && mktgBreakEvenMonths > 0
                              ? 'repeat bookings recover the gap, then compound'
                              : 'check repeat rate data'}
                          </p>
                        </div>
                      </div>

                      {/* P&L table */}
                      <div className="overflow-x-auto mb-5">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="text-left text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal w-44">Revenue stream</th>
                              <th className="text-right text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal">No mktg</th>
                              <th className="text-right text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal">Month 1</th>
                              <th className="text-right text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal">Month 6</th>
                              <th className="text-right text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal">Month 12</th>
                              <th className="text-right text-white/20 text-[10px] uppercase tracking-widest pb-2 font-normal">Month 24</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-white/[0.04]">
                              <td className="py-2.5 text-brand-400/70 text-[10px]">Hold fees (organic baseline)</td>
                              <td className="py-2.5 text-right text-brand-400/60">{fmtK(baselineHoldFees)}</td>
                              <td className="py-2.5 text-right text-brand-400/60">{fmtK(baselineHoldFees)}</td>
                              <td className="py-2.5 text-right text-brand-400/60">{fmtK(baselineHoldFees)}</td>
                              <td className="py-2.5 text-right text-brand-400/60">{fmtK(baselineHoldFees)}</td>
                              <td className="py-2.5 text-right text-brand-400/60">{fmtK(baselineHoldFees)}</td>
                            </tr>
                            <tr className="border-b border-white/[0.04]">
                              <td className="py-2.5 text-brand-300/60 text-[10px]">+ Hold fees from mktg bookings</td>
                              <td className="py-2.5 text-right text-white/20">—</td>
                              <td className="py-2.5 text-right text-brand-300/70">{fmtK(mktgHoldM1)}</td>
                              <td className="py-2.5 text-right text-brand-300/70">{fmtK(mktgHoldM6)}</td>
                              <td className="py-2.5 text-right text-brand-300/70">{fmtK(mktgHoldM12)}</td>
                              <td className="py-2.5 text-right text-brand-300/80 font-medium">{fmtK(mktgHoldM24)}</td>
                            </tr>
                            <tr className="border-b border-white/[0.04]">
                              <td className="py-2.5 text-white/30 text-[10px]">− Marketing spend</td>
                              <td className="py-2.5 text-right text-white/20">—</td>
                              <td className="py-2.5 text-right text-white/30">{fmtK(marketingBudget)}</td>
                              <td className="py-2.5 text-right text-white/30">{fmtK(marketingBudget)}</td>
                              <td className="py-2.5 text-right text-white/30">{fmtK(marketingBudget)}</td>
                              <td className="py-2.5 text-right text-white/30">{fmtK(marketingBudget)}</td>
                            </tr>
                            <tr className="bg-white/[0.02]">
                              <td className="py-3 text-white/60 text-[10px] font-normal">= Investor net/mo</td>
                              <td className={`py-3 text-right font-medium ${netColor(baselineHoldFees)}`}>{fmtNet(baselineHoldFees)}</td>
                              <td className={`py-3 text-right font-medium ${netColor(totalNetM1)}`}>{fmtNet(totalNetM1)}</td>
                              <td className={`py-3 text-right font-medium ${netColor(totalNetM6)}`}>{fmtNet(totalNetM6)}</td>
                              <td className={`py-3 text-right font-medium ${netColor(totalNetM12)}`}>{fmtNet(totalNetM12)}</td>
                              <td className={`py-3 text-right font-medium text-base ${netColor(totalNetM24)}`}>{fmtNet(totalNetM24)}</td>
                            </tr>
                            <tr className="border-t border-white/[0.04]">
                              <td className="pt-2 pb-1 text-white/20 text-[9px]">recurring bookings/mo</td>
                              <td className="pt-2 pb-1 text-right text-white/20 text-[9px]">—</td>
                              <td className="pt-2 pb-1 text-right text-emerald-400/40 text-[9px]">0</td>
                              <td className="pt-2 pb-1 text-right text-emerald-400/50 text-[9px]">{fmt(repeatM6, 1)}</td>
                              <td className="pt-2 pb-1 text-right text-emerald-400/60 text-[9px]">{fmt(repeatM12, 1)}</td>
                              <td className="pt-2 pb-1 text-right text-emerald-400/80 text-[9px] font-normal">{fmt(repeatM24, 1)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Key stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Capital Revolving (mo 12)</p>
                          <p className="text-white text-base font-normal">{fmtK(capitalM12)}</p>
                        </div>
                        <div className="p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Monthly Marketing</p>
                          <p className="text-white text-base font-normal">{fmtK(marketingBudget)}</p>
                          <p className="text-white/20 text-[9px]">constant — never increases</p>
                        </div>
                        <div className="p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Net uplift mo 1→12</p>
                          <p className="text-emerald-400 text-base font-normal">+{fmtK(totalNetM12 - totalNetM1)}</p>
                          <p className="text-white/20 text-[9px]">same budget · more recurring</p>
                        </div>
                        <div className="p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest mb-1">Net uplift mo 1→24</p>
                          <p className="text-emerald-400 text-base font-normal">+{fmtK(totalNetM24 - totalNetM1)}</p>
                          <p className="text-white/20 text-[9px]">compounding over 2 years</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </motion.div>

            {/* ── Partnership Structure ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-4 h-4 text-brand-400" />
                <h2 className="text-lg font-light text-white tracking-tight">Partnership Structure</h2>
              </div>
              <p className="text-white/60 text-sm font-normal mb-7">
                This is a capital partnership — not equity. The investor earns a royalty on the installment volume their capital supports, with full return of principal as bookings repay.
              </p>

              {/* Structure overview cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                  <p className="text-white/20 text-xs font-mono mb-3">01</p>
                  <p className="text-white text-sm font-normal mb-2">Revenue Share Agreement</p>
                  <p className="text-white/65 text-sm font-normal leading-relaxed">
                    Investor enters a Revenue Share Agreement with Defera LLC — not equity, not a loan in the traditional sense. The investor earns a royalty (hold fee) on every installment booking their capital funds. No board seat, no operational control, no equity stake.
                  </p>
                </div>
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                  <p className="text-white/20 text-xs font-mono mb-3">02</p>
                  <p className="text-white text-sm font-normal mb-2">Return of Capital & Acquisition Rights</p>
                  <p className="text-white/65 text-sm font-normal leading-relaxed">
                    All deployed capital is returned as customers complete their installment payments. If Defera is ever acquired, the investor receives <span className="text-emerald-400/80">30% of the acquisition proceeds</span> plus full reimbursement of any capital still deployed at the time of sale. No equity — but protected upside on an exit.
                  </p>
                </div>
                <div className="p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                  <p className="text-white/20 text-xs font-mono mb-3">03</p>
                  <p className="text-white text-sm font-normal mb-2">Live Earnings Dashboard</p>
                  <p className="text-white/65 text-sm font-normal leading-relaxed">
                    The investor will have access to a real-time dashboard — updated as bookings come in — showing capital deployed, bookings funded, hold fees earned, and repayments received. Full visibility, no black box.
                  </p>
                </div>
              </div>

              {/* Mechanics */}
              <div className="p-6 rounded-2xl border border-brand-500/15 bg-brand-500/[0.03]">
                <p className="text-brand-400/60 text-[10px] uppercase tracking-widest font-normal mb-5">How Funds Move</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-brand-400 text-[9px] font-normal">1</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Funding</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          Investor wires capital to <span className="text-white/80">Defera LLC</span> via wire transfer or ACH. Funds are held and drawn down as bookings are made.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-brand-400 text-[9px] font-normal">2</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Deployment</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          As customers book flights on installment, capital is deployed per booking. The hold fee is collected from the customer across their payment schedule.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-brand-400 text-[9px] font-normal">3</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Returns</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          As each installment is paid, principal is returned to the capital pool and hold fees are credited to the investor. Earnings can be withdrawn or reinvested.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-400 text-[9px] font-normal">4</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Acquisition Clause</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          If Defera is acquired, the investor receives <span className="text-emerald-400/80">30% of the acquisition proceeds</span> plus full reimbursement of any capital still deployed at close. This is contractually separate from the hold fee royalty.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-brand-400 text-[9px] font-normal">5</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Withdrawals</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          Capital not currently deployed on an active installment can be recalled with <span className="text-white/80">30 days' written notice</span>. Deployed capital returns naturally as bookings repay — it is not called early.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-brand-400 text-[9px] font-normal">6</span>
                      </div>
                      <div>
                        <p className="text-white/85 text-xs font-normal mb-0.5">Visibility</p>
                        <p className="text-white/60 text-[13px] font-normal leading-relaxed">
                          Investor gets a live dashboard with real-time booking activity, capital utilisation, and earnings — full transparency into how the pool is performing at any moment.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key terms summary strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/5">
                  {[
                    { label: 'Structure', value: 'Revenue Share' },
                    { label: 'Equity', value: 'None' },
                    { label: 'Acquisition share', value: '30%' },
                    { label: 'Withdrawal notice', value: '30 days' },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <p className="text-white text-base font-normal">{value}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* ── Footnotes ── */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="pt-8 border-t border-white/5"
            >
              <p className="text-white/20 text-[11px] font-normal leading-relaxed max-w-2xl">
                This document is strictly confidential and intended solely for the recipient. All financial data is pulled
                live from the Defera platform and reflects all bookings. Hold fee rates: standard installments 15%,
                Defera Capital 15%, Reserve 0%. Service fees are tiered by base fare amount.
                Projections are illustrative and not a guarantee of future returns.
              </p>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Root Export ───────────────────────────────────────────────────────
function MobileGate() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-8 text-center" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(15,40,138,0.25) 0%, #000 60%)' }}>
      <div className="max-w-xs w-full">
        <img src="/defera-logo-web.png" alt="Defera" className="h-8 w-auto opacity-80 mx-auto mb-8" />
        <h1 className="text-white text-xl font-light tracking-tight mb-3">Desktop only</h1>
        <p className="text-white/60 text-sm font-normal leading-relaxed">
          This investor overview contains detailed financial data and interactive tools that require a desktop browser to view properly.
        </p>
        <p className="text-white/20 text-xs font-normal mt-4">
          Please open this link on a laptop or desktop to continue.
        </p>
      </div>
    </div>
  );
}

export default function InvestorPitch() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(PIN_SESSION_KEY) === '1');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (isMobile) return <MobileGate />;

  return (
    <AnimatePresence mode="wait">
      {unlocked ? (
        <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <InvestorDashboard />
        </motion.div>
      ) : (
        <motion.div key="gate" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
          <PinGate onUnlock={() => setUnlocked(true)} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
